import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, RefreshCw, Users as UsersIcon, UserCheck, UserX, ShieldCheck, Eye } from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import { api, apiBaseUrl, getErrorMessage } from "@/lib/api";
import AdminPager from "@/components/admin/AdminPager";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUS_FILTERS = [{ id: "", label: "All" }, { id: "active", label: "Active" }, { id: "suspended", label: "Suspended" }, { id: "banned", label: "Banned" }, { id: "restricted", label: "Restricted" }];
const KYC_FILTERS = [{ id: "", label: "All KYC" }, { id: "approved", label: "Approved" }, { id: "pending_review", label: "Pending" }, { id: "rejected", label: "Rejected" }, { id: "not_started", label: "Not started" }];

export default function AdminUsers() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [kycFilter, setKycFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const pager = useAdminPager();
  const [listTotal, setListTotal] = useState(0);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = { skip: pager.skip, limit: pager.limit };
      if (statusFilter) params.status = statusFilter;
      if (kycFilter) params.kyc = kycFilter;
      if (q) params.q = q;
      const { data } = await api.get("/api/admin/users", { params });
      const { items, total } = unwrapAdminList(data, "users");
      // Never render more than one page of rows (guards old APIs that ignore limit).
      setUsers(asAdminList(items).slice(0, pager.pageSize));
      pager.setTotal(total);
      setListTotal(total);
    } catch (e) {
      setUsers([]);
      pager.setTotal(0);
      setListTotal(0);
      setErr(getErrorMessage(e));
    }
    setLoading(false);
  }
  useEffect(() => { pager.resetPage(); }, [statusFilter, kycFilter, pager.resetPage]);
  useEffect(() => { load(); }, [statusFilter, kycFilter, pager.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = asAdminList(users);
  const total = listTotal;
  const active = rows.filter((u) => (u.status || "active") === "active").length;
  const suspended = rows.filter((u) => u.status === "suspended" || u.status === "banned").length;
  const kycApproved = rows.filter((u) => u.kyc_status === "approved").length;

  const columns = [
    { key: "user", header: "User", render: (u) => (
      <div>
        <div className="font-semibold text-foreground">{u.full_name || "—"}</div>
        <div className="text-[11px] text-muted-foreground">{u.email}</div>
      </div>
    )},
    { key: "mobile", header: "Mobile", render: (u) => <span className="text-muted-foreground">{u.mobile_number || "—"}</span> },
    { key: "status", header: "Status", render: (u) => <StatusPill value={u.status || "active"} /> },
    { key: "kyc", header: "KYC", render: (u) => <StatusPill value={u.kyc_status} /> },
    { key: "tier", header: "Tier", render: (u) => <span className="text-muted-foreground">{u.tier || "T1"}</span> },
    { key: "created_at", header: "Joined", render: (u) => <span className="text-muted-foreground text-[11px]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span> },
    { key: "actions", header: "Actions", align: "right", render: (u) => (
      <button onClick={() => nav(`/admin/users/${u.id}`)} data-testid={`view-${u.id}`} className="text-primary hover:text-primary inline-flex items-center gap-1 text-[12px] font-semibold">
        <Eye size={12} /> View
      </button>
    )},
  ];

  return (
    <AdminShell title="Users" subtitle="Open a user for wallets, KYC, trades, sessions and admin actions.">
      {/* Same layout pattern as Blog: sticky pager at bottom of panel */}
      <div className="flex flex-col flex-1 min-h-0 gap-5">
        <div className="grid shrink-0 grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard label="Users (filtered)" value={total} icon={UsersIcon} variant="yellow" testid="kpi-total" />
          <KpiCard label="Active (this page)" value={active} icon={UserCheck} variant="green" />
          <KpiCard label="Suspended / Banned" value={suspended} icon={UserX} variant="red" />
          <KpiCard label="KYC Approved" value={kycApproved} icon={ShieldCheck} variant="purple" />
        </div>

        <Panel className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="shrink-0 space-y-3 border-b border-border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex min-w-[240px] flex-1 items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, email, mobile" data-testid="users-search" className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground" />
              </div>
              <Btn kind="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
              <a href={`${apiBaseUrl()}/api/admin/reports/users.csv`} target="_blank" rel="noreferrer">
                <Btn kind="primary"><Download size={13} /> Export CSV</Btn>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Status</span>
                <FilterChips options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} testIdPrefix="status" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">KYC</span>
                <FilterChips options={KYC_FILTERS} value={kycFilter} onChange={setKycFilter} testIdPrefix="kyc" />
              </div>
            </div>
          </div>

          {err ? (
            <p className="px-4 pt-3 text-red-400 text-[13px] shrink-0" data-testid="users-error">{err}</p>
          ) : null}

          <div className="flex-1 min-h-0 overflow-auto">
            {loading && rows.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">Loading…</div>
            ) : (
              <DataTable columns={columns} rows={rows} testid="users-table" empty="No users found." rowKey={(u) => u.id} />
            )}
          </div>

          <AdminPager
            page={pager.page}
            total={pager.total}
            pageSize={pager.pageSize}
            loading={loading}
            onPage={pager.setPage}
            testId="users-pager"
            emptyLabel="No users"
            sticky
          />
        </Panel>
      </div>
    </AdminShell>
  );
}
